import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import styles from "./ChannelAnalysis.module.scss";
import { Card, Table, Select, Input, Typography, Skeleton, Empty } from "antd";
import {
  ArrowUpOutlined,
  ArrowDownOutlined,
  SearchOutlined,
  RightOutlined,
} from "@ant-design/icons";
import {
  ChannelAnalysisLanding,
  ChannelAnalysisLandingItem,
  getChannelAnalysisLanding,
} from "../../../api/Services";
import {
  useDebouncedValue,
  useFetchRequestGuard,
} from "../../../helpers/FilterSearchHelper";
import {
  isVariationAvailable,
  variationNotAvailableLabel,
} from "../../../helpers/VariationHelper";

const { Title, Text } = Typography;

const fmt = (v?: number | null) =>
  `$${(v ?? 0).toLocaleString("es-AR", { maximumFractionDigits: 0 })}`;
const fmtN = (v?: number | null) => (v ?? 0).toLocaleString("es-AR");

const Var: React.FC<{ value?: number | null; suffix?: string }> = ({
  value,
  suffix = "%",
}) => {
  if (!isVariationAvailable(value)) return <span>{variationNotAvailableLabel()}</span>;
  const pos = value >= 0;
  return (
    <span
      className={`${styles.variation} ${
        pos ? styles.positive : styles.negative
      }`}
    >
      {pos ? <ArrowUpOutlined /> : <ArrowDownOutlined />}{" "}
      {pos ? "+" : ""}
      {value.toFixed(1)}
      {suffix}
    </span>
  );
};

const MIX_COLORS = ["#8b5cf6", "#3b82f6", "#10b981", "#f59e0b", "#ef4444"];

const emptyLanding: ChannelAnalysisLanding = { channels: [], mix: [] };

const LandingTableSkeleton: React.FC = () => (
  <div className={styles.tableSkeleton}>
    {Array.from({ length: 6 }).map((_, i) => (
      <Skeleton
        key={i}
        active
        title={false}
        paragraph={{ rows: 1, width: ["100%"] }}
      />
    ))}
  </div>
);

const ChannelAnalysis: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search);
  const [period, setPeriod] = useState(30);
  const [channelIds, setChannelIds] = useState<number[]>([]);
  const [sortField, setSortField] = useState("ventaBruta");
  const [sortOrder, setSortOrder] = useState<"ascend" | "descend">("descend");
  const [data, setData] = useState<ChannelAnalysisLanding>(emptyLanding);
  const [channelOptions, setChannelOptions] = useState<
    { value: number; label: string }[]
  >([]);
  const [loading, setLoading] = useState(true);
  const { beginRequest, isStaleRequest } = useFetchRequestGuard();

  const handleFetch = useCallback(async () => {
    const requestId = beginRequest();
    setLoading(true);
    const result = await getChannelAnalysisLanding({
      search: debouncedSearch || undefined,
      channelIds: channelIds.length ? channelIds : undefined,
      period,
      sortBy: sortField,
      sortOrder: sortOrder === "ascend" ? "asc" : "desc",
    });
    if (isStaleRequest(requestId)) return;
    setData(result);
    setChannelOptions((prev) => {
      const map = new Map(prev.map((item) => [item.value, item]));
      result.channels.forEach((channel) =>
        map.set(channel.id, { value: channel.id, label: channel.name })
      );
      return Array.from(map.values());
    });
    setLoading(false);
  }, [
    debouncedSearch,
    channelIds,
    period,
    sortField,
    sortOrder,
    beginRequest,
    isStaleRequest,
  ]);

  useEffect(() => {
    handleFetch();
  }, [handleFetch]);

  const rows = useMemo(() => data.channels || [], [data.channels]);
  const hasData = rows.length > 0;

  const handleTableChange = (_p: any, _f: any, sorter: any) => {
    if (sorter.columnKey) {
      setSortField(sorter.columnKey);
      setSortOrder(sorter.order || "descend");
    }
  };

  const columns = [
    {
      title: t("ChannelAnalysis-Channel"),
      key: "name",
      render: (_: any, r: ChannelAnalysisLandingItem) => (
        <Text strong className={styles.channelName}>
          {r.name}
          <RightOutlined />
        </Text>
      ),
    },
    {
      title: t("ChannelAnalysis-GrossSales"),
      key: "ventaBruta",
      sorter: true,
      sortOrder: sortField === "ventaBruta" ? sortOrder : undefined,
      align: "right" as const,
      render: (_: any, r: ChannelAnalysisLandingItem) => (
        <div className={styles.cellStack}>
          <Text strong>{fmt(r.ventaBruta)}</Text>
          <Var value={r.ventaVar} />
        </div>
      ),
    },
    {
      title: t("ChannelAnalysis-Units"),
      key: "unidades",
      sorter: true,
      sortOrder: sortField === "unidades" ? sortOrder : undefined,
      align: "right" as const,
      render: (_: any, r: ChannelAnalysisLandingItem) => (
        <div className={styles.cellStack}>
          <Text strong>{fmtN(r.unidades)}</Text>
          <Var value={r.unidadesVar} />
        </div>
      ),
    },
    {
      title: t("ChannelAnalysis-AverageTicket"),
      key: "ticketPromedio",
      align: "right" as const,
      sorter: true,
      sortOrder: sortField === "ticketPromedio" ? sortOrder : undefined,
      render: (_: any, r: ChannelAnalysisLandingItem) => (
        <div className={styles.cellStack}>
          <Text strong>{fmt(r.ticketPromedio)}</Text>
          <Var value={r.ticketPromedioVar} />
        </div>
      ),
    },
    {
      title: t("ChannelAnalysis-BasketSize"),
      key: "tamanoCesta",
      align: "right" as const,
      sorter: true,
      sortOrder: sortField === "tamanoCesta" ? sortOrder : undefined,
      render: (_: any, r: ChannelAnalysisLandingItem) => (
        <div className={styles.cellStack}>
          <Text strong>{(r.tamanoCesta ?? 0).toFixed(2)}</Text>
          <Var value={r.tamanoCestaVar} />
        </div>
      ),
    },
    {
      title: t("ChannelAnalysis-Margin"),
      key: "margen",
      sorter: true,
      sortOrder: sortField === "margen" ? sortOrder : undefined,
      align: "right" as const,
      render: (_: any, r: ChannelAnalysisLandingItem) => (
        <div className={styles.cellStack}>
          <Text strong>{(r.margenPct ?? 0).toFixed(1)}%</Text>
          <Var value={r.margenVar} suffix=" pp" />
        </div>
      ),
    },
    {
      title: t("ChannelAnalysis-Stock"),
      key: "stockStatus",
      align: "center" as const,
      width: 130,
      render: () => <Text type="secondary">—</Text>,
    },
    {
      title: t("ChannelAnalysis-Breaks"),
      key: "quiebres",
      align: "right" as const,
      width: 100,
      render: () => <Text type="secondary">—</Text>,
    },
  ];

  return (
    <div className={styles.wrapper}>
      <Title level={3} className={styles.pageTitle}>
        {t("ChannelAnalysis-Channels")}
      </Title>

      <Card className={styles.filtersCard} size="small">
        <div className={styles.filtersRow}>
          <Input
            placeholder={t("ChannelAnalysis-SearchChannel")}
            prefix={<SearchOutlined />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={styles.searchInput}
            allowClear
          />
          <div className={styles.filterItem}>
            <Text className={styles.filterLabel}>{t("ChannelAnalysis-Channel")}</Text>
            <Select
              placeholder={t("ChannelAnalysis-AllChannels")}
              mode="multiple"
              allowClear
              value={channelIds}
              onChange={setChannelIds}
              className={styles.filterSelect}
              maxTagCount={1}
              options={channelOptions}
            />
          </div>
          <div className={styles.filterItem}>
            <Text className={styles.filterLabel}>{t("ChannelAnalysis-Period")}</Text>
            <Select
              value={period}
              onChange={setPeriod}
              className={styles.filterSelect}
              options={[
                { value: 30, label: t("ChannelAnalysis-Last30Days") },
                { value: 60, label: t("ChannelAnalysis-Last60Days") },
                { value: 90, label: t("ChannelAnalysis-Last90Days") },
                { value: 180, label: t("ChannelAnalysis-Last180Days") },
              ]}
            />
          </div>
        </div>
      </Card>

      <Card className={styles.tableCard}>
        {loading && !hasData ? (
          <LandingTableSkeleton />
        ) : (
          <Table
            columns={columns}
            dataSource={rows.map((c) => ({ ...c, key: c.id }))}
            pagination={false}
            size="small"
            className={styles.dataTable}
            loading={loading && hasData}
            onChange={handleTableChange}
            locale={{
              emptyText: (
                <Empty description={t("ChannelAnalysis-NoChannels")} />
              ),
            }}
            onRow={(record) => ({
              onClick: () =>
                navigate(`/commercial-analysis/channels/${record.id}`),
              style: { cursor: "pointer" },
            })}
          />
        )}
      </Card>

      <Card className={styles.mixCard}>
        <div className={styles.mixHeader}>
          <Title level={5}>{t("ChannelAnalysis-SalesMix")}</Title>
          <Text>{t("ChannelAnalysis-SalesMixDescription")}</Text>
        </div>
        {loading && !(data.mix || []).length ? (
          <Skeleton active title={false} paragraph={{ rows: 1, width: ["100%"] }} />
        ) : (
          <>
            <div className={styles.mixBar}>
              {(data.mix || []).map((c, i) => (
                <div
                  key={c.channelId}
                  className={styles.mixSegment}
                  style={{
                    width: `${c.pctVenta}%`,
                    backgroundColor: MIX_COLORS[i % MIX_COLORS.length],
                  }}
                  title={`${c.channelName}: ${c.pctVenta.toFixed(1)}%`}
                />
              ))}
            </div>
            <div className={styles.mixLegend}>
              {(data.mix || []).map((c, i) => (
                <div key={c.channelId} className={styles.mixLegendItem}>
                  <span
                    className={styles.mixDot}
                    style={{
                      backgroundColor: MIX_COLORS[i % MIX_COLORS.length],
                    }}
                  />
                  <Text>{c.channelName}</Text>
                  <Text strong style={{ marginLeft: 4 }}>
                    {c.pctVenta.toFixed(1)}%
                  </Text>
                </div>
              ))}
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

export default ChannelAnalysis;
